import { DeleteOutlined, LoadingOutlined } from '@ant-design/icons';
import { Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle } from '@mui/material';
import VenueTypeDto from 'common/dto-types/venue-type-dto';
import IconButton from 'components/@extended/IconButton';
import { useState } from 'react';
import { dispatch } from 'store';
import { openSnackbar } from 'store/reducers/snackbar';
import { deleteVenueType } from './api';

type VenueTypeDeleteButtonProps = {
    venueType: VenueTypeDto;
    refreshCallback: () => Promise<void>;
};

const VenueTypeDeleteButton = ({ venueType, refreshCallback }: VenueTypeDeleteButtonProps): JSX.Element => {
    const [isLoading, setIsLoading] = useState(false);
    const [isOpen, setIsOpen] = useState(false);

    const handleClose = () => {
        setIsOpen(false);
    };

    const handleDelete = async () => {
        setIsLoading(true);

        const response = await deleteVenueType(venueType.id);

        if (response?.status === 200) {
            dispatch(
                openSnackbar({
                    open: true,
                    message: 'Venue Type deleted successfully!',
                    variant: 'alert',
                    alert: {
                        color: 'success',
                    },
                }),
            );
            await refreshCallback();
        } else {
            const references = response?.data?.references;
            const referenceDetail = references ? ` (Venues: ${references.venues})` : '';
            dispatch(
                openSnackbar({
                    open: true,
                    message: `${response?.data?.message ?? 'Cannot delete Venue Type!'}${referenceDetail}`,
                    variant: 'alert',
                    alert: {
                        color: 'error',
                    },
                }),
            );
        }

        setIsLoading(false);
        setIsOpen(false);
    };

    if (isLoading) return <LoadingOutlined style={{ padding: '9px' }} />;

    return (
        <>
            <IconButton onClick={() => setIsOpen(true)} shape="rounded" color="error">
                <DeleteOutlined />
            </IconButton>
            <Dialog open={isOpen} onClose={handleClose} aria-labelledby="responsive-dialog-title">
                <DialogTitle id="responsive-dialog-title">
                    Are you sure you want to delete Venue Type &quot;{venueType.name}&quot;?
                </DialogTitle>
                <DialogContent>
                    <DialogContentText>Deleting is irreversible! Proceed?</DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button autoFocus onClick={handleClose}>
                        Cancel
                    </Button>
                    <Button onClick={handleDelete} autoFocus color="error" disabled={isLoading}>
                        Delete
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default VenueTypeDeleteButton;
